import { memo } from 'react'

import { Autocomplete, Chip, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import { SmepLiveDelayStrategyModeEnum } from '@nativewaves/nw-cloud-sdk/smep'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { NumberField } from '../NumberField'
import type { ConfigFormValues } from '../types'

const PanelRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { control } = useFormContext<ConfigFormValues>()

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="heading-md" sx={{ mb: 2 }}>
        {t('domain:Workspace.Event.ConfigEditor.general')}
      </Typography>
      <Stack spacing={3}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => <TextField {...field} size="small" label={t('name')} />}
        />
        <Controller
          control={control}
          name="tags"
          render={({ field }) => (
            <Autocomplete
              multiple
              freeSolo
              options={[] as string[]}
              value={field.value ?? []}
              onChange={(_, value) => field.onChange(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  return <Chip key={key} size="small" label={option} {...tagProps} />
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('domain:Workspace.Event.ConfigEditor.tags')}
                  placeholder={t('domain:Workspace.Event.ConfigEditor.addTag')}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="fixtureId"
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              sx={{ maxWidth: 320 }}
              label={t('domain:Workspace.Event.ConfigEditor.fixtureId')}
            />
          )}
        />
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Controller
            control={control}
            name="liveDelayStrategy.mode"
            render={({ field }) => (
              <TextField
                {...field}
                select
                size="small"
                sx={{ minWidth: 220 }}
                label={t('domain:Workspace.Event.ConfigEditor.liveDelayMode')}
              >
                {Object.values(SmepLiveDelayStrategyModeEnum).map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {mode}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="liveDelayStrategy.realTimeDelay"
            render={({ field }) => (
              <NumberField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                size="small"
                label={t('domain:Workspace.Event.ConfigEditor.realTimeDelay')}
              />
            )}
          />
          <Controller
            control={control}
            name="liveDelayStrategy.playbackDelay"
            render={({ field }) => (
              <NumberField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                size="small"
                label={t('domain:Workspace.Event.ConfigEditor.playbackDelay')}
              />
            )}
          />
          <Controller
            control={control}
            name="pointZeroRealTimeReferenceShift"
            render={({ field }) => (
              <NumberField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                size="small"
                sx={{ minWidth: 220 }}
                label={t('domain:Workspace.Event.ConfigEditor.pointZeroShift')}
              />
            )}
          />
        </Stack>
      </Stack>
    </Paper>
  )
}

export const Panel = memo(PanelRaw)
